import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { AppRoot } from "./app/AppRoot";
import { loadConfig } from "./config";
import { I18nProvider } from "./i18n/localization";
import { readSessionToken } from "./services/authToken";
import "./styles.css";

// Bootstrap (docs/design/modules/04-web-client.md §3). Host-mode detection happens
// inside AppRoot; main.tsx only loads the runtime config and mounts React.

// Reset the host document for a full-viewport app even when the served page did NOT link
// the extracted global CSS — the app-service launch page loads only the JS bundle, so the
// styles.css reset is absent and the UA's default body margin (~16px) makes the document
// scroll. Applied to <html> and <body> (which inline element styles cannot reach). Mirrors
// the styles.css reset, so it is a no-op when that CSS is present.
function resetHostDocument(): void {
  for (const el of [document.documentElement, document.body]) {
    el.style.margin = "0";
    el.style.height = "100%";
    el.style.overflow = "hidden";
  }
}

async function start() {
  resetHostDocument();
  // Persist a launch token from the entry URL (?token=) into sessionStorage once, at boot,
  // before any routing happens. In-SPA navigation uses plain <Link>s that drop the query
  // string, so without this a valid token would be lost when moving to /admin, /schedule,
  // etc. Capturing it here keeps the token out of subsequent URLs/history (sessionStorage is
  // the carrier) while ensuring authorized navigation never silently loses the session.
  readSessionToken(new URLSearchParams(window.location.search));
  await loadConfig();
  const root = ReactDOM.createRoot(document.getElementById("root")!);
  root.render(
    <React.StrictMode>
      <BrowserRouter>
        <I18nProvider>
          <AppRoot />
        </I18nProvider>
      </BrowserRouter>
    </React.StrictMode>
  );
}

start().catch((err) => {
  const lang = (navigator.languages?.[0] ?? navigator.language ?? "").toLowerCase();
  document.body.innerText = lang.startsWith("he")
    ? "אירעה תקלה בטעינת המערכת — נא לרענן או לפנות לתמיכה."
    : "The system failed to load. Please refresh or contact support.";
  // eslint-disable-next-line no-console
  console.error(err);
});
